import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  Share,
  Star,
  MoreVertical,
  FileText,
  Image,
  Video,
  Music,
  Archive,
  File,
  Maximize2,
  Minimize2,
  RotateCw,
  ZoomIn,
  ZoomOut,
  ChevronLeft,
  ChevronRight,
  BookOpen,
  Search,
  Bookmark,
  Printer,
  Copy,
  ExternalLink,
  Eye,
  EyeOff
} from 'lucide-react';

import { useToast } from '../../hooks/useToast';
import ToastContainer from '../../components/ui/ToastContainer';
import PDFViewer from '../../components/ui/PDFViewer';
import PowerPointViewer from '../../components/ui/PowerPointViewer';
import ExcelViewer from '../../components/ui/ExcelViewer';
import EnhancedExcelViewer from '../../components/ui/EnhancedExcelViewer';
import AdvancedExcelViewer from '../../components/ui/AdvancedExcelViewer';
import ExcelReaderViewer from '../../components/ui/ExcelReaderViewer';
import SimpleExcelReader from '../../components/ui/SimpleExcelReader';
import RobustExcelViewer from '../../components/ui/RobustExcelViewer';
import LocalExcelViewer from '../../components/ui/LocalExcelViewer';
import TabbedExcelViewer from '../../components/ui/TabbedExcelViewer';
import EnhancedTableExcelViewer from '../../components/ui/EnhancedTableExcelViewer';
import WordViewer from '../../components/ui/WordViewer';
import EnhancedWordViewer from '../../components/ui/EnhancedWordViewer';
import AdvancedWordViewer from '../../components/ui/AdvancedWordViewer';
import SimpleWordViewer from '../../components/ui/SimpleWordViewer';
import FallbackViewer from '../../components/ui/FallbackViewer';
import EnhancedPDFViewer from '../../components/ui/EnhancedPDFViewer';
import ImprovedPDFViewer from '../../components/ui/ImprovedPDFViewer';
import SimplePDFViewer from '../../components/ui/SimplePDFViewer';
import PDFJSViewer from '../../components/ui/PDFJSViewer';
import GoogleDocsPDFViewer from '../../components/ui/GoogleDocsPDFViewer';
import LocalPDFViewer from '../../components/ui/LocalPDFViewer';
import ProxyPDFViewer from '../../components/ui/ProxyPDFViewer';
import { apiService } from '../../services/api';
import '../../styles/background-effects.css';


interface DocumentViewerPageProps {
  documentId?: string;
  onClose?: () => void;
}

interface DocumentData {
  id: string;
  name: string;
  title?: string;
  file_type: string;
  file_size: number;
  content?: string;
  description?: string;
  file_url?: string;
  cloud_url?: string;
  uploaded_at: string;
  uploaded_by?: string;
  tags?: string[];
}

const DocumentViewerPage: React.FC<DocumentViewerPageProps> = ({
  documentId: propDocumentId,
  onClose: propOnClose
}) => {
  const { documentId: urlDocumentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  
  // Use URL parameter if available, otherwise use prop
  const documentId = urlDocumentId || propDocumentId;
  const onClose = useCallback(() => {
    if (propOnClose) {
      propOnClose();
    } else {
      navigate('/workspace/documents');
    }
  }, [propOnClose, navigate]);
  
  console.log('📄 DocumentViewerPage - documentId reçu:', documentId);
  console.log('📄 DocumentViewerPage - urlDocumentId:', urlDocumentId);
  console.log('📄 DocumentViewerPage - propDocumentId:', propDocumentId);
  const [document, setDocument] = useState<DocumentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<number[]>([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);
  const [showBookmarks, setShowBookmarks] = useState(false);
  const [bookmarks, setBookmarks] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showPageNumbers, setShowPageNumbers] = useState(true);
  const [fontSize, setFontSize] = useState(16);
  const [theme, setTheme] = useState<'light' | 'dark' | 'sepia'>('light');
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [pdfViewerMode, setPdfViewerMode] = useState<'text' | 'pdf'>('pdf');
  const [powerPointViewerMode, setPowerPointViewerMode] = useState<'text' | 'presentation'>('text');
  const [excelViewerMode, setExcelViewerMode] = useState<'text' | 'spreadsheet'>('text');
  const [wordViewerMode, setWordViewerMode] = useState<'text' | 'document'>('text');
  const [fallbackMode, setFallbackMode] = useState<'native' | 'fallback'>('native');
  const [pdfViewerType, setPdfViewerType] = useState<'enhanced' | 'improved' | 'simple' | 'pdfjs' | 'google' | 'local' | 'proxy' | 'fallback'>('pdfjs');
  
  const { toasts, showSuccess, showError, removeToast } = useToast();
  const contentRef = useRef<HTMLDivElement>(null);
  const hasLoadedRef = useRef(false);

  // Animation variants
  const fadeUp = (delay: number) => ({
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5, delay }
  });

  const hoverElevate = {
    y: -2,
    transition: { type: "tween" as const, duration: 0.15 }
  };

  const tapPress = {
    scale: 0.98,
    transition: { type: "tween" as const, duration: 0.1 }
  };

  // Load document data
  useEffect(() => {
    // Reset le flag quand le documentId change
    hasLoadedRef.current = false;
    
    const loadDocument = async () => {
      // Éviter le chargement multiple
      if (hasLoadedRef.current) {
        console.log('📄 Document déjà chargé, ignoré');
        return;
      }
      
      hasLoadedRef.current = true;
      setLoading(true);
      try {
        console.log('📄 Chargement du document:', documentId);
        console.log('📄 Type du documentId:', typeof documentId);
        
        // Validation de l'ID du document
        const documentIdStr = String(documentId);
        console.log('📄 documentIdStr:', documentIdStr);
        
        if (!documentId || documentIdStr.trim() === '') {
          console.error('❌ ID du document invalide:', documentId);
          showError('Erreur de chargement', 'ID du document invalide');
          onClose();
          return;
        }
        
        console.log('📄 Appel de l\'API pour récupérer les documents...');
        
        // Test de l'API avec gestion d'erreur améliorée
        let documents;
        try {
          documents = await apiService.getDocuments();
          console.log('📄 Documents récupérés:', documents.length);
          console.log('📄 IDs des documents disponibles:', documents.map(d => ({ id: d.id, name: d.name || d.title })));
          
          // Stocker les informations de débogage
          setDebugInfo({
            documentId: documentIdStr,
            documentsCount: documents.length,
            availableIds: documents.map(d => ({ id: d.id, name: d.name || d.title })),
            timestamp: new Date().toISOString()
          });
        } catch (apiError) {
          console.error('❌ Erreur API:', apiError);
          const errorMessage = apiError instanceof Error ? apiError.message : String(apiError) || 'Erreur inconnue';
          setDebugInfo({
            documentId: documentIdStr,
            error: errorMessage,
            timestamp: new Date().toISOString()
          });
          showError('Erreur API', 'Impossible de récupérer les documents depuis le serveur. Vérifiez que le backend est en cours d\'exécution.');
          onClose();
          return;
        }
        
        const doc = documents.find(d => String(d.id) === documentIdStr);
        console.log('📄 Document trouvé:', doc ? 'OUI' : 'NON');
        
        if (doc) {
          // Construire l'URL du fichier si elle n'est pas présente
          if (!doc.file_url && doc.id) {
            // Construire l'URL du fichier à partir de l'ID
            const baseUrl = window.location.origin;
            doc.file_url = `${baseUrl}/api/v1/documents/${doc.id}/file/`;
            console.log('📄 URL du fichier construite:', doc.file_url);
          }
          
          setDocument(doc);
          console.log('✅ Document trouvé:', doc.title || doc.name);
          console.log('📄 file_url:', doc.file_url);
          
          // Calculer le nombre de pages approximatif basé sur le contenu
          if (doc.content) {
            const wordsPerPage = 250; // Estimation
            const words = doc.content.split(/\s+/).length;
            setTotalPages(Math.max(1, Math.ceil(words / wordsPerPage)));
            console.log('📄 Pages calculées:', Math.max(1, Math.ceil(words / wordsPerPage)));
          } else {
            console.log('⚠️ Aucun contenu disponible pour le document');
            setTotalPages(1);
          }
        } else {
          console.error('❌ Document non trouvé avec l\'ID:', documentId);
          console.error('❌ IDs disponibles:', documents.map(d => d.id));
          showError('Document non trouvé', `Le document avec l'ID ${documentId} n'existe pas. Veuillez vérifier l'URL ou retourner à la liste des documents.`);
          // Attendre un peu avant de fermer pour que l'utilisateur puisse lire le message
          setTimeout(() => {
            onClose();
          }, 3000);
        }
        
        console.log('✅ Document chargé avec succès');
      } catch (error) {
        console.error('❌ Erreur lors du chargement du document:', error);
        showError('Erreur de chargement', 'Impossible de charger le document');
        onClose();
      } finally {
        setLoading(false);
      }
    };

    if (documentId) {
      loadDocument();
    } else {
      console.error('❌ Aucun documentId fourni');
      showError('Erreur de chargement', 'Aucun document spécifié');
      onClose();
    }
  }, [documentId]);

  // Get file icon based on type
  const getFileIcon = (type: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      'pdf': <FileText className="w-8 h-8 text-red-500" />,
      'docx': <FileText className="w-8 h-8 text-blue-500" />,
      'doc': <FileText className="w-8 h-8 text-blue-500" />,
      'pptx': <FileText className="w-8 h-8 text-orange-500" />,
      'ppt': <FileText className="w-8 h-8 text-orange-500" />,
      'xlsx': <FileText className="w-8 h-8 text-green-500" />,
      'xls': <FileText className="w-8 h-8 text-green-500" />,
      'txt': <FileText className="w-8 h-8 text-gray-500" />,
      'jpg': <Image className="w-8 h-8 text-purple-500" />,
      'jpeg': <Image className="w-8 h-8 text-purple-500" />,
      'png': <Image className="w-8 h-8 text-purple-500" />,
      'gif': <Image className="w-8 h-8 text-purple-500" />,
      'mp4': <Video className="w-8 h-8 text-pink-500" />,
      'avi': <Video className="w-8 h-8 text-pink-500" />,
      'mp3': <Music className="w-8 h-8 text-indigo-500" />,
      'wav': <Music className="w-8 h-8 text-indigo-500" />,
      'zip': <Archive className="w-8 h-8 text-yellow-500" />,
      'rar': <Archive className="w-8 h-8 text-yellow-500" />
    };
    return iconMap[type.toLowerCase()] || <File className="w-8 h-8 text-gray-500" />;
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Get display name for file type
  const getFileTypeDisplayName = (type: string) => {
    const typeMap: Record<string, string> = {
      'pdf': 'Document PDF',
      'docx': 'Document Word',
      'doc': 'Document Word',
      'xlsx': 'Feuille de calcul Excel',
      'xls': 'Feuille de calcul Excel',
      'pptx': 'Présentation PowerPoint',
      'ppt': 'Présentation PowerPoint',
      'txt': 'Fichier texte',
      'md': 'Document Markdown',
      'jpg': 'Image JPEG',
      'jpeg': 'Image JPEG',
      'png': 'Image PNG',
      'gif': 'Image GIF'
    };
    return typeMap[type?.toLowerCase()] || type?.toUpperCase() || 'Fichier';
  };

  // Handle file operations
  const handleDownload = async () => {
    if (!document) return;
    
    try {
      showSuccess('Téléchargement en cours...');
      
      if (document.file_url || document.cloud_url) {
        const link = window.document.createElement('a');
        const url = document.file_url || document.cloud_url;
        if (url) {
          link.href = url;
          link.download = document.name;
          link.target = '_blank';
          window.document.body.appendChild(link);
          link.click();
          window.document.body.removeChild(link);
          showSuccess('Fichier téléchargé avec succès');
        }
      } else {
        const blob = new Blob([document.content || 'Contenu non disponible'], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const link = window.document.createElement('a');
        link.href = url;
        link.download = document.name.replace(/\.[^/.]+$/, '.txt');
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
        URL.revokeObjectURL(url);
        showSuccess('Contenu exporté en fichier texte');
      }
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
      showError('Erreur de téléchargement', 'Impossible de télécharger le fichier');
    }
  };

  const handleShare = () => {
    const url = `${window.location.origin}/documents/${documentId}`;
    navigator.clipboard.writeText(url);
    showSuccess('Lien de partage copié dans le presse-papiers');
  };

  const handleToggleFavorite = () => {
    setIsFavorite(!isFavorite);
    showSuccess(
      isFavorite ? 'Retiré des favoris' : 'Ajouté aux favoris'
    );
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 25, 300));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 25, 25));
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const handleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleToggleBookmark = () => {
    const newBookmarks = bookmarks.includes(currentPage)
      ? bookmarks.filter(page => page !== currentPage)
      : [...bookmarks, currentPage];
    setBookmarks(newBookmarks);
    showSuccess(
      bookmarks.includes(currentPage) 
        ? 'Signet supprimé' 
        : 'Signet ajouté'
    );
  };

  const handleSearch = () => {
    if (!searchQuery.trim() || !document?.content) return;
    
    const content = document.content.toLowerCase();
    const query = searchQuery.toLowerCase();
    const results: number[] = [];
    
    let index = content.indexOf(query);
    while (index !== -1) {
      results.push(index);
      index = content.indexOf(query, index + 1);
    }
    
    setSearchResults(results);
    setCurrentSearchIndex(0);
  };

  const handleNextSearch = () => {
    if (searchResults.length === 0) return;
    setCurrentSearchIndex(prev => (prev + 1) % searchResults.length);
  };

  const handlePrevSearch = () => {
    if (searchResults.length === 0) return;
    setCurrentSearchIndex(prev => (prev - 1 + searchResults.length) % searchResults.length);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleCopy = () => {
    if (document?.content) {
      navigator.clipboard.writeText(document.content);
      showSuccess('Contenu copié dans le presse-papiers');
    }
  };

  // Render document content based on file type
  const renderDocumentContent = () => {
    if (!document) return null;

    const content = document.content || document.description || 'Aucun contenu disponible';
    
    // Debug: Afficher les informations sur le contenu
    console.log('📄 Contenu du document:', {
      hasContent: !!document.content,
      contentLength: document.content ? document.content.length : 0,
      hasDescription: !!document.description,
      descriptionLength: document.description ? document.description.length : 0,
      finalContent: content.substring(0, 100) + '...'
    });

    // Rendu spécialisé par type de fichier - Mode lecture native
    switch (document.file_type?.toLowerCase()) {
      case 'pdf':
        // Toujours utiliser le visualiseur PDF natif (pas le texte brut)
        return renderNativePDFViewer();
      
      case 'docx':
      case 'doc':
        return renderNativeWordViewer();
      
      case 'xlsx':
      case 'xls':
        return renderNativeExcelViewer();
      
      case 'pptx':
      case 'ppt':
        return renderNativePowerPointViewer();
      
      case 'txt':
      case 'md':
        return renderTextViewer(content);
      
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return renderImageViewer();
      
      default:
        return renderGenericViewer(content);
    }
  };

  // Visualiseur PDF natif - Mode lecture directe
  const renderNativePDFViewer = () => {
    // Construire l'URL du fichier si elle n'est pas présente
    let pdfUrl = document?.file_url;
    if (!pdfUrl && document?.id) {
      // Construire l'URL à partir de l'ID du document
      const baseUrl = window.location.origin;
      pdfUrl = `${baseUrl}/api/v1/documents/${document.id}/file/`;
    }
    
    if (!pdfUrl) {
      return (
        <div className="w-full h-full flex items-center justify-center">
          <div className="text-center">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">PDF non disponible</h3>
            <p className="text-gray-500">Le fichier PDF n'est pas accessible pour la lecture.</p>
          </div>
        </div>
      );
    }
    
    // Utiliser un iframe pour afficher directement le PDF (méthode la plus simple et fiable)
    // Le navigateur affichera le PDF nativement avec ses propres contrôles (zoom, téléchargement, etc.)
    return (
      <div className="w-full h-full flex flex-col bg-gray-100" style={{ height: 'calc(100vh - 80px)' }}>
        <iframe
          src={pdfUrl}
          className="w-full h-full border-0"
          title={`Lecture de ${document?.name || 'document'}`}
          style={{ 
            minHeight: 'calc(100vh - 80px)',
            width: '100%',
            border: 'none'
          }}
          onError={() => {
            console.error('Erreur de chargement du PDF');
            showError('Erreur de chargement', 'Impossible de charger le PDF. Vérifiez que le fichier existe.');
          }}
        />
      </div>
    );

    // Sélectionner le type de visualiseur PDF
    switch (pdfViewerType) {
      case 'enhanced':
        return (
          <EnhancedPDFViewer
            document={document}
            onDownload={handleDownload}
          />
        );
      
      case 'improved':
        return (
          <ImprovedPDFViewer
            document={document}
            onDownload={handleDownload}
          />
        );
      
      case 'simple':
        return (
          <SimplePDFViewer
            document={document}
            onDownload={handleDownload}
          />
        );
      
      case 'pdfjs':
        return (
          <PDFJSViewer
            document={document}
            onDownload={handleDownload}
          />
        );
      
      case 'google':
        return (
          <GoogleDocsPDFViewer
            document={document}
            onDownload={handleDownload}
          />
        );
      
      case 'local':
        return (
          <LocalPDFViewer
            document={document}
            onDownload={handleDownload}
          />
        );
      
      case 'proxy':
        return (
          <ProxyPDFViewer
            document={document}
            onDownload={handleDownload}
          />
        );
      
      case 'fallback':
        return (
          <FallbackViewer
            document={document}
            fileType="pdf"
            onDownload={handleDownload}
            onNativeViewer={() => setPdfViewerType('local')}
          />
        );
      
      default:
        return (
          <PDFJSViewer
            document={document}
            onDownload={handleDownload}
          />
        );
    }
  };

  // Visualiseur Word natif - Mode lecture directe
  const renderNativeWordViewer = () => {
    return (
      <SimpleWordViewer
        document={document}
        onDownload={handleDownload}
      />
    );
  };

  // Visualiseur Excel natif - Mode lecture directe
  const renderNativeExcelViewer = () => {
    return (
      <TabbedExcelViewer
        document={document}
        onDownload={handleDownload}
      />
    );
  };

  // Visualiseur PowerPoint natif - Mode lecture directe
  const renderNativePowerPointViewer = () => {
    if (!document?.file_url) {
      return (
        <div className="w-full h-full flex items-center justify-center">
          <div className="text-center">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Présentation PowerPoint non disponible</h3>
            <p className="text-gray-500">Le fichier PowerPoint n'est pas accessible pour la lecture.</p>
          </div>
        </div>
      );
    }

    return (
      <div className="w-full h-full flex flex-col">
        {/* Header avec contrôles */}
        <div className="bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <FileText className="w-5 h-5 text-orange-500" />
            <span className="font-medium text-gray-700">{document.name}</span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleDownload}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm"
            >
              <Download className="w-4 h-4 mr-2 inline" />
              Télécharger
            </button>
            <button
              onClick={() => window.open(document.file_url, '_blank')}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
            >
              <ExternalLink className="w-4 h-4 mr-2 inline" />
              Ouvrir dans PowerPoint
            </button>
          </div>
        </div>

        {/* Lecteur PowerPoint avec fallback intelligent */}
        <div className="flex-1 bg-gray-100">
          <iframe
            src={document.file_url}
            className="w-full h-full border-0"
            title={`Lecture de ${document.name}`}
            onError={() => {
              console.error('Erreur de chargement de la présentation PowerPoint natif');
              showError('Erreur de chargement', 'Impossible de charger la présentation PowerPoint natif. Affichage du contenu extrait.');
            }}
          />
        </div>
        
        {/* Fallback - Contenu extrait */}
        <div className="bg-white border-t border-gray-200 p-4">
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-2">
              Si la présentation PowerPoint ne s'affiche pas correctement, vous pouvez :
            </p>
            <div className="flex justify-center space-x-2">
              <button
                onClick={handleDownload}
                className="px-3 py-1 bg-orange-100 text-orange-700 rounded text-sm hover:bg-orange-200"
              >
                Télécharger le fichier
              </button>
              <button
                onClick={() => window.open(document.file_url, '_blank')}
                className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200"
              >
                Ouvrir dans PowerPoint
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Visualiseur de texte
  // Fonction pour formater le contenu texte de manière lisible
  const formatTextContent = (content: string): string => {
    if (!content) return '';
    
    // Échapper les caractères HTML pour éviter les injections
    const escapeHtml = (text: string) => {
      const div = window.document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    };
    
    // Diviser en paragraphes (lignes vides)
    const paragraphs = content.split(/\n\s*\n/).filter(p => p.trim().length > 0);
    
    // Formater chaque paragraphe
    const formattedParagraphs = paragraphs.map(paragraph => {
      // Nettoyer les espaces multiples
      const cleaned = paragraph.trim().replace(/\s+/g, ' ');
      
      // Détecter les titres (lignes courtes, en majuscules, ou commençant par #)
      const isTitle = cleaned.length < 100 && (
        cleaned === cleaned.toUpperCase() ||
        cleaned.startsWith('#') ||
        /^[A-Z][^.!?]*$/.test(cleaned)
      );
      
      if (isTitle) {
        return `<h3 class="text-xl font-bold text-gray-900 mt-6 mb-3 pb-2 border-b border-gray-200">${escapeHtml(cleaned.replace(/^#+\s*/, ''))}</h3>`;
      }
      
      // Détecter les listes (lignes commençant par -, *, ou numéros)
      if (/^[-*•]\s/.test(cleaned) || /^\d+[\.\)]\s/.test(cleaned)) {
        const listItems = paragraph.split(/\n/).filter(item => item.trim().length > 0);
        const listHtml = listItems.map(item => {
          const cleanedItem = item.trim().replace(/^[-*•]\s*/, '').replace(/^\d+[\.\)]\s*/, '');
          return `<li class="mb-2 ml-4">${escapeHtml(cleanedItem)}</li>`;
        }).join('');
        return `<ul class="list-disc space-y-2 my-4">${listHtml}</ul>`;
      }
      
      // Paragraphe normal
      return `<p class="mb-4 text-gray-700 leading-relaxed">${escapeHtml(cleaned)}</p>`;
    });
    
    return formattedParagraphs.join('');
  };

  const renderTextViewer = (content: string) => {
    return (
      <div 
        ref={contentRef}
        className={`max-w-4xl mx-auto ${theme === 'dark' ? 'bg-gray-900 text-gray-100' : 'bg-white'} ${theme === 'sepia' ? 'bg-amber-50' : ''}`}
        style={{ 
          fontSize: `${fontSize}px`,
          lineHeight: '1.8',
          padding: '2rem'
        }}
      >
        <div 
          className="document-content"
          dangerouslySetInnerHTML={{
            __html: formatTextContent(content)
          }}
        />
      </div>
    );
  };

  // Visualiseur d'image
  const renderImageViewer = () => {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <Image className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">Visualisation d'image</p>
          <div className="flex space-x-2">
            <button
              onClick={handleDownload}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download className="w-4 h-4 mr-2 inline" />
              Télécharger l'image
            </button>
            <button
              onClick={() => window.open(document?.file_url, '_blank')}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <ExternalLink className="w-4 h-4 mr-2 inline" />
              Voir l'image
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Visualiseur générique
  const renderGenericViewer = (content: string) => {
    return (
      <div 
        ref={contentRef}
        className={`max-w-4xl mx-auto ${theme === 'dark' ? 'bg-gray-900 text-gray-100' : 'bg-white'} ${theme === 'sepia' ? 'bg-amber-50' : ''}`}
        style={{ 
          fontSize: `${fontSize}px`,
          lineHeight: '1.8',
          padding: '2rem'
        }}
      >
        <div 
          className="document-content"
          dangerouslySetInnerHTML={{
            __html: formatTextContent(content)
          }}
        />
      </div>
    );
  };

  // Debug: Afficher les informations de débogage
  console.log('🔍 DocumentViewerPage - documentId final:', documentId);
  console.log('🔍 DocumentViewerPage - loading:', loading);
  console.log('🔍 DocumentViewerPage - document:', document);

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement du document...</p>
          <p className="text-sm text-gray-500 mt-2">ID: {documentId}</p>
        </div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="fixed inset-0 z-50 bg-white flex items-center justify-center">
        <div className="text-center max-w-2xl mx-auto p-6">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Document non trouvé</h3>
          <p className="text-gray-600 mb-4">Le document demandé n'existe pas ou n'est plus disponible.</p>
          
          {/* Panneau de débogage */}
          {debugInfo && (
            <div className="bg-gray-50 rounded-lg p-4 mb-4 text-left">
              <h4 className="font-bold text-gray-800 mb-2">🔍 Informations de débogage</h4>
              <div className="text-sm text-gray-600 space-y-1">
                <p><strong>Document ID recherché:</strong> {debugInfo.documentId}</p>
                <p><strong>Documents disponibles:</strong> {debugInfo.documentsCount || 'Erreur lors du chargement'}</p>
                {debugInfo.availableIds && debugInfo.availableIds.length > 0 && (
                  <div>
                    <p><strong>IDs disponibles:</strong></p>
                    <ul className="ml-4 text-xs max-h-32 overflow-y-auto">
                      {debugInfo.availableIds.map((item: any, index: number) => (
                        <li key={index} className="py-1">• ID: {item.id} - Nom: {item.name}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {debugInfo.error && (
                  <p className="text-red-600"><strong>Erreur API:</strong> {debugInfo.error}</p>
                )}
                <p className="text-xs text-gray-400 mt-2">Timestamp: {new Date(debugInfo.timestamp).toLocaleString()}</p>
              </div>
            </div>
          )}
          
          <button
            onClick={onClose}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            Retour aux documents
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-white">
      {/* Aurora Background Effect */}
      
      <div className={`relative z-10 bg-white ${isFullscreen ? 'fixed inset-0' : 'h-full'}`}>
        {/* Header */}
        <motion.header 
          className="bg-white shadow-sm border-b border-gray-200 relative z-50"
          {...fadeUp(0)}
        >
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center space-x-4">
              <motion.button
                onClick={onClose}
                whileHover={hoverElevate}
                whileTap={tapPress}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                title="Retour"
              >
                <ArrowLeft className="w-5 h-5" />
              </motion.button>
              
              <div className="flex items-center space-x-3">
                {getFileIcon(document.file_type)}
                <div>
                  <h1 className="text-lg font-semibold text-gray-900 truncate max-w-md">
                    {document.title || document.name}
                  </h1>
                  <p className="text-sm text-gray-500">
                    {formatFileSize(document.file_size)} • {document.file_type.toUpperCase()}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              {/* Zoom controls */}
              <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
                <motion.button
                  onClick={handleZoomOut}
                  whileHover={hoverElevate}
                  whileTap={tapPress}
                  className="p-2 rounded text-gray-600 hover:bg-white transition-colors"
                  title="Zoom arrière"
                >
                  <ZoomOut className="w-4 h-4" />
                </motion.button>
                <span className="text-sm font-medium text-gray-700 px-2">
                  {zoom}%
                </span>
              <motion.button
                onClick={handleZoomIn}
                whileHover={hoverElevate}
                whileTap={tapPress}
                className="p-2 rounded text-gray-600 hover:bg-white transition-colors"
                title="Zoom avant"
              >
                <ZoomIn className="w-4 h-4" />
              </motion.button>
            </div>

            {/* Boutons de sélection du visualiseur PDF */}
            {document?.file_type?.toLowerCase() === 'pdf' && (
              <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1 ml-4">
                <motion.button
                  onClick={() => setPdfViewerType('local')}
                  whileHover={hoverElevate}
                  whileTap={tapPress}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                    pdfViewerType === 'local' 
                      ? 'bg-blue-500 text-white' 
                      : 'text-gray-600 hover:bg-white'
                  }`}
                  title="Utiliser le lecteur local"
                >
                  Local
                </motion.button>
                <motion.button
                  onClick={() => setPdfViewerType('proxy')}
                  whileHover={hoverElevate}
                  whileTap={tapPress}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                    pdfViewerType === 'proxy' 
                      ? 'bg-blue-500 text-white' 
                      : 'text-gray-600 hover:bg-white'
                  }`}
                  title="Utiliser le lecteur proxy"
                >
                  Proxy
                </motion.button>
                <motion.button
                  onClick={() => setPdfViewerType('enhanced')}
                  whileHover={hoverElevate}
                  whileTap={tapPress}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                    pdfViewerType === 'enhanced' 
                      ? 'bg-blue-500 text-white' 
                      : 'text-gray-600 hover:bg-white'
                  }`}
                  title="Utiliser PDF.js"
                >
                  PDF.js
                </motion.button>
                <motion.button
                  onClick={() => setPdfViewerType('google')}
                  whileHover={hoverElevate}
                  whileTap={tapPress}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                    pdfViewerType === 'google' 
                      ? 'bg-blue-500 text-white' 
                      : 'text-gray-600 hover:bg-white'
                  }`}
                  title="Utiliser Google Docs Viewer"
                >
                  Google
                </motion.button>
                <motion.button
                  onClick={() => setPdfViewerType('fallback')}
                  whileHover={hoverElevate}
                  whileTap={tapPress}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                    pdfViewerType === 'fallback' 
                      ? 'bg-blue-500 text-white' 
                      : 'text-gray-600 hover:bg-white'
                  }`}
                  title="Utiliser le mode fallback"
                >
                  Fallback
                </motion.button>
              </div>
            )}

              {/* Font size controls */}
              <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setFontSize(prev => Math.max(12, prev - 2))}
                  className="p-2 rounded text-gray-600 hover:bg-white transition-colors"
                  title="Réduire la police"
                >
                  A
                </button>
                <span className="text-sm font-medium text-gray-700 px-2">
                  {fontSize}px
                </span>
                <button
                  onClick={() => setFontSize(prev => Math.min(24, prev + 2))}
                  className="p-2 rounded text-gray-600 hover:bg-white transition-colors"
                  title="Agrandir la police"
                >
                  A
                </button>
              </div>

              {/* Theme controls */}
              <select
                value={theme}
                onChange={(e) => setTheme(e.target.value as 'light' | 'dark' | 'sepia')}
                className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="light">Clair</option>
                <option value="dark">Sombre</option>
                <option value="sepia">Sépia</option>
              </select>

              {/* Rotation */}
              <motion.button
                onClick={handleRotate}
                whileHover={hoverElevate}
                whileTap={tapPress}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                title="Rotation"
              >
                <RotateCw className="w-5 h-5" />
              </motion.button>

              {/* Fullscreen */}
              <motion.button
                onClick={handleFullscreen}
                whileHover={hoverElevate}
                whileTap={tapPress}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                title={isFullscreen ? "Sortir du plein écran" : "Plein écran"}
              >
                {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
              </motion.button>

              {/* Actions */}
              <motion.button
                onClick={handleToggleBookmark}
                whileHover={hoverElevate}
                whileTap={tapPress}
                className={`p-2 rounded-lg transition-colors ${
                  bookmarks.includes(currentPage)
                    ? 'text-yellow-500 hover:bg-yellow-50' 
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                }`}
                title="Ajouter un signet"
              >
                <Bookmark className={`w-5 h-5 ${bookmarks.includes(currentPage) ? 'fill-current' : ''}`} />
              </motion.button>

              <motion.button
                onClick={handleToggleFavorite}
                whileHover={hoverElevate}
                whileTap={tapPress}
                className={`p-2 rounded-lg transition-colors ${
                  isFavorite 
                    ? 'text-yellow-500 hover:bg-yellow-50' 
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                }`}
                title={isFavorite ? "Retirer des favoris" : "Ajouter aux favoris"}
              >
                <Star className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
              </motion.button>

              <motion.button
                onClick={handleCopy}
                whileHover={hoverElevate}
                whileTap={tapPress}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                title="Copier le contenu"
              >
                <Copy className="w-5 h-5" />
              </motion.button>

              <motion.button
                onClick={handlePrint}
                whileHover={hoverElevate}
                whileTap={tapPress}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                title="Imprimer"
              >
                <Printer className="w-5 h-5" />
              </motion.button>

              <motion.button
                onClick={handleShare}
                whileHover={hoverElevate}
                whileTap={tapPress}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                title="Partager"
              >
                <Share className="w-5 h-5" />
              </motion.button>

              <motion.button
                onClick={handleDownload}
                whileHover={hoverElevate}
                whileTap={tapPress}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                title="Télécharger"
              >
                <Download className="w-5 h-5" />
              </motion.button>

              <motion.button
                onClick={() => setShowSidebar(!showSidebar)}
                whileHover={hoverElevate}
                whileTap={tapPress}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                title="Afficher les outils"
              >
                <MoreVertical className="w-5 h-5" />
              </motion.button>
            </div>
          </div>
        </motion.header>

        {/* Sidebar */}
        <AnimatePresence>
          {showSidebar && (
            <motion.div
              initial={{ x: -300, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -300, opacity: 0 }}
              className="fixed left-0 top-16 bottom-0 w-80 bg-white border-r border-gray-200 shadow-lg z-40 overflow-y-auto"
            >
              <div className="p-4 space-y-4">
                {/* Search */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Recherche dans le document</h3>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                      placeholder="Rechercher..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    />
                    <button
                      onClick={handleSearch}
                      className="px-3 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                    >
                      <Search className="w-4 h-4" />
                    </button>
                  </div>
                  {searchResults.length > 0 && (
                    <div className="mt-2 flex items-center space-x-2">
                      <span className="text-sm text-gray-600">
                        {currentSearchIndex + 1} sur {searchResults.length}
                      </span>
                      <button
                        onClick={handlePrevSearch}
                        className="p-1 text-gray-400 hover:text-gray-600"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={handleNextSearch}
                        className="p-1 text-gray-400 hover:text-gray-600"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Bookmarks */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Signets</h3>
                  <div className="space-y-1">
                    {bookmarks.length === 0 ? (
                      <p className="text-sm text-gray-500">Aucun signet</p>
                    ) : (
                      bookmarks.map((page, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentPage(page)}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                        >
                          Page {page}
                        </button>
                      ))
                    )}
                  </div>
                </div>

                {/* Page Navigation */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Navigation</h3>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-sm text-gray-600">
                      Page {currentPage} sur {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Document Info */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Informations</h3>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p><strong>Type:</strong> {document.file_type.toUpperCase()}</p>
                    <p><strong>Taille:</strong> {formatFileSize(document.file_size)}</p>
                    <p><strong>Pages:</strong> {totalPages}</p>
                    {document.uploaded_at && (
                      <p><strong>Date:</strong> {new Date(document.uploaded_at).toLocaleDateString()}</p>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main content */}
        <motion.main 
          className={`${isFullscreen ? 'h-screen' : 'h-[calc(100vh-4rem)]'} overflow-auto ${showSidebar ? 'ml-80' : ''}`}
          {...fadeUp(0.1)}
        >
          <div 
            className={`p-6 ${theme === 'dark' ? 'bg-gray-900 text-white' : theme === 'sepia' ? 'bg-amber-50' : 'bg-white'}`}
            style={{ 
              transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
              transformOrigin: 'center center'
            }}
          >
            <div className="max-w-4xl mx-auto">
              {renderDocumentContent()}
            </div>
          </div>
        </motion.main>

        {/* Page numbers */}
        <AnimatePresence>
          {showPageNumbers && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="fixed bottom-4 right-4 bg-white rounded-lg shadow-lg px-4 py-2 border border-gray-200"
            >
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowPageNumbers(false)}
                  className="p-1 text-gray-400 hover:text-gray-600"
                >
                  <EyeOff className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-600">
                  {currentPage} / {totalPages}
                </span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {!showPageNumbers && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={() => setShowPageNumbers(true)}
            className="fixed bottom-4 right-4 p-2 bg-white rounded-lg shadow-lg border border-gray-200 text-gray-400 hover:text-gray-600"
          >
            <Eye className="w-4 h-4" />
          </motion.button>
        )}
      </div>

      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </div>
  );
};

export default DocumentViewerPage;
